import { useCallback, useContext, useEffect } from 'react';
import { create } from 'zustand';

import { AuthContext } from '../../../providers/AuthProvider';
import { ApiMethod } from '../../../common/models/api-types.type';
import { routes } from '../../../assets/constants';
import { UsagePair, readUsage } from './plan-limits';

/** `GET plans/my-plan`, as far as the meters need it. */
export interface MyPlanResponse {
  plan?: { name?: string; slug?: string; pricingType?: string; limits?: Record<string, unknown>; features?: Record<string, unknown> };
  usage?: Record<string, { current: number; limit: number }>;
}

/** Re-read after this long: usage moves while the vendor works, but not by the second. */
const FRESH_MS = 60_000;

interface UsageState {
  data: MyPlanResponse | null;
  loading: boolean;
  fetchedAt: number;
  set: (patch: Partial<Omit<UsageState, 'set'>>) => void;
}

const useUsageStore = create<UsageState>((set) => ({
  data: null,
  loading: false,
  fetchedAt: 0,
  set: (patch) => set(patch),
}));

let inflight: Promise<void> | null = null;

type Send = (method: ApiMethod, path: string) => Promise<unknown>;

function load(send: Send, force = false): Promise<void> {
  const { fetchedAt, set } = useUsageStore.getState();
  if (!force && Date.now() - fetchedAt < FRESH_MS) return Promise.resolve();
  if (inflight) return inflight;
  set({ loading: true });
  inflight = send(ApiMethod.GET, routes.plans.myPlan)
    .then((res) => set({ data: (res as MyPlanResponse) ?? null, fetchedAt: Date.now() }))
    // A failed read shows no meters rather than wrong ones.
    .catch(() => set({ fetchedAt: Date.now() }))
    .finally(() => {
      inflight = null;
      set({ loading: false });
    });
  return inflight;
}

/** For tests: forget what was read. */
export const resetPlanUsage = () => {
  inflight = null;
  useUsageStore.setState({ data: null, loading: false, fetchedAt: 0 });
};

/** For tests and stories: pretend the API answered. */
export const primePlanUsage = (data: MyPlanResponse | null) =>
  useUsageStore.setState({ data, loading: false, fetchedAt: Date.now() });

/**
 * The vendor's usage against their plan, shared by every meter on screen.
 *
 * One request for the whole app (deduplicated, cached for a minute) rather
 * than one per meter. Outside the auth provider — or against an API that does
 * not answer — it reports nothing, and meters that read it render nothing.
 */
export function usePlanUsage(options: { enabled?: boolean } = {}) {
  const enabled = options.enabled !== false;
  const send = useContext(AuthContext)?.sendAuthGuardedRequest as Send | undefined;
  const data = useUsageStore((s) => s.data);
  const loading = useUsageStore((s) => s.loading);

  useEffect(() => {
    if (!enabled || typeof send !== 'function') return;
    load(send);
  }, [enabled, send]);

  const refresh = useCallback(() => (typeof send === 'function' ? load(send, true) : Promise.resolve()), [send]);
  const meter = useCallback((key: string): UsagePair | null => readUsage(data?.usage, key), [data]);

  return { data, loading, meter, refresh };
}
